// Board — 共享职位板 (the shared job board).
//
// Chinese-first by design: this is the UK-CN product's entry surface, and its
// users are Chinese students in the UK. The rest of the app stays English.
// It is also PUBLIC — anonymous visitors browse it before registering, so the
// anonymous view carries job information and nothing else: no buttons, no
// product pitch. Rows navigate to the public detail page (/jobs/:id), where
// the conversion card lives. Signed-in users additionally get the row-end
// evaluate button, because their core loop is rapid bulk evaluation.
//
// Unlike Matches, this reads ONE GLOBAL table (/api/board), not the caller's
// own scan history — a brand-new signup sees a populated board immediately
// instead of having to run their own scan first.
//
// Filter semantics, deliberate; see web/lib/repos/job-board.mjs for the
// measurements behind them: role_level / industry are derived from the title,
// which is always present, so "unclassified" is real evidence and those rows
// are excluded. The "＋N 未分类" toggle sends include_unknown=true to opt
// back in.
//
// Filter state lives in the URL (searchParams), not useState: coming back
// from a detail page restores the exact view via history, and a filtered
// board is shareable as a link. Only the search-input draft stays local.

import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import Chip from '../components/ui/Chip';
import { apiGet } from '../lib/api';
import { useZh, useProductReady, useSignedIn } from '../lib/useProduct';
import { allCombos, useAnchorCounts, useAnchors, usePositions } from '../components/domain/anchors';
import {
  ROLE_LABELS, INDUSTRY_LABELS, salaryText, daysAgo, displayCompany,
  AddToPipeline, JobTitle, useContentLang,
} from '../components/domain/board';
import type { RoleLevel, BoardResponse, Facets } from '../components/domain/board';

const PAGE_SIZE = 50;

interface CVStatus { file: string | null; chars: number; preview: string }

export default function Board() {
  // UK-CN-only surface. With the product flag off, this route doesn't exist —
  // but only once we know which product this is; see useProductReady.
  const zh = useZh();
  const productReady = useProductReady();
  const signedIn = useSignedIn();
  const nav = useNavigate();

  const lang = useContentLang();
  const [sp, setSp] = useSearchParams();
  const roleLevel = (sp.get('role') || '') as RoleLevel | '';
  const industry = sp.get('ind') || '';
  const includeUnknown = sp.get('unknown') === '1';
  const sponsorOnly = sp.get('vs') === '1';
  const search = sp.get('q') || '';
  const page = Math.max(0, parseInt(sp.get('page') || '0', 10) || 0);
  // The input's draft, distinct from the applied search term in the URL.
  const [q, setQ] = useState(search);

  // Every filter change goes through here. replace:true — filter clicks must
  // not pile up as history entries, or backing out of the board means
  // stepping through every chip you ever touched. The detail-page navigation
  // is a push, so back still returns to this exact filtered view.
  const patch = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    next.delete('page'); // any filter change resets pagination
    if ('page' in changes && changes.page) next.set('page', changes.page);
    setSp(next, { replace: true });
  };

  // Signed-in only (the PublicShell rule: anonymous visitors must trigger
  // zero personal queries here). Drives the import-CV card below — the
  // Shell's thin banner yields to it on this page.
  const cvQ = useQuery({
    queryKey: ['cv-status'],
    queryFn: () => apiGet<CVStatus>('/api/cv-status'),
    staleTime: 60_000,
    enabled: signedIn,
  });
  const needsCv = signedIn && cvQ.isSuccess && !((cvQ.data?.chars ?? 0) > 100);

  // 「为你推荐」 — the user's CV-derived title anchors, applied here as a
  // DISPLAY filter. Editing lives in the shared AnchorEditor (same component
  // as the 简历 page) so the two surfaces never drift.
  const a = useAnchors(signedIn);
  const pos = usePositions(signedIn);
  // The combos ARE the filter — same list the 简历 page's builder previews.
  const combos = allCombos(a.anchors, pos.positions, pos.combos);
  const rec = sp.get('rec') === '1' && combos.length > 0;
  const countsQ = useAnchorCounts(combos, signedIn && rec);

  const params = new URLSearchParams();
  if (roleLevel) params.set('role_level', roleLevel);
  if (industry) params.set('industry', industry);
  if (includeUnknown) params.set('include_unknown', 'true');
  if (sponsorOnly) params.set('sponsor_only', '1');
  if (search) params.set('q', search);
  if (rec) params.set('anchors', combos.join(','));
  // Order by when the EMPLOYER posted, not when our scanner first saw it.
  // A board is refilled in bulk, so every row from one ingest shares a
  // first_seen and the default sort collapses to alphabetical-by-URL — which
  // put 75-day-old listings at the top and made the board look abandoned.
  params.set('sort', 'posted');
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(page * PAGE_SIZE));

  const boardQ = useQuery({
    queryKey: ['board', roleLevel, industry, includeUnknown, sponsorOnly, search, page, rec ? combos.join(',') : ''],
    queryFn: () => apiGet<BoardResponse>(`/api/board?${params.toString()}`),
    staleTime: 60_000,
    // Filter clicks keep the previous page on screen (dimmed below) instead
    // of collapsing the list into a loading card — same idiom as Matches.
    placeholderData: (prev) => prev,
  });

  const facetsQ = useQuery({
    queryKey: ['board-facets'],
    queryFn: () => apiGet<Facets>('/api/board/facets'),
    staleTime: 5 * 60_000,
  });

  const items = boardQ.data?.items ?? [];
  const total = boardQ.data?.total ?? 0;
  const facets = facetsQ.data;
  const unknownCount = facets?.roleLevel?.unknown ?? 0;
  const pages = Math.ceil(total / PAGE_SIZE);

  // Redirect only once we actually know the product — otherwise a direct link
  // to /board bounces to /matches during the first render, every time.
  if (!productReady) return null;
  if (!zh) return <Navigate to="/matches" replace />;

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="共享职位板"
        title="英国实习与毕业生职位"
        sub={
          facets
            ? `共 ${facets.total.toLocaleString()} 个职位 · 每日自动更新`
            : '加载中…'
        }
      />

      {/* 筛选 */}
      <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="eyebrow" style={{ minWidth: 56, marginBottom: 0 }}>类型</span>
          <Chip variant={roleLevel === '' ? 'primary' : 'default'} onClick={() => patch({ role: null })}>
            全部
          </Chip>
          {(Object.keys(ROLE_LABELS) as RoleLevel[]).map((k) => (
            <Chip
              key={k}
              variant={roleLevel === k ? 'primary' : 'default'}
              onClick={() => patch({ role: roleLevel === k ? null : k })}
            >
              {ROLE_LABELS[k]}
              {facets?.roleLevel?.[k] != null && ` · ${facets.roleLevel[k].toLocaleString()}`}
            </Chip>
          ))}
          {/* 未分类的职位默认不显示：role_level 是从标题推导的，标题总是存在，
              所以"未分类"意味着标题看起来不像实习/毕业生岗，是真实的否定证据。 */}
          {(roleLevel || industry) && unknownCount > 0 && (
            <Chip
              variant={includeUnknown ? 'third' : 'default'}
              onClick={() => patch({ unknown: includeUnknown ? null : '1' })}
              title="这些职位的标题看不出是不是实习/毕业生岗。打开后一并显示。"
            >
              {includeUnknown ? '✓ ' : '＋ '}
              未分类 {unknownCount.toLocaleString()}
            </Chip>
          )}
          {roleLevel === 'internship' && (
            <span className="page-sub" style={{ marginTop: 0, fontSize: 12 }}>
              学生签打工上限：学期中每周 20 小时（gov.uk 规定）
            </span>
          )}
        </div>

        {/* 签证 — the gov.uk sponsor register as a FILTER (plain words per
            user: 可以办工签). Public data, so anonymous visitors get it too. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="eyebrow" style={{ minWidth: 56, marginBottom: 0 }}>签证</span>
          <Chip
            variant={sponsorOnly ? 'primary' : 'default'}
            onClick={() => patch({ vs: sponsorOnly ? null : '1' })}
            title="这些雇主在 gov.uk 官方担保人名录上，有资格帮外国员工办 Skilled Worker 工签（按公司名精确匹配）。毕业生转工签的新人工资门槛为 £33,400"
          >
            🛡 可以办工签的雇主
          </Chip>
          {sponsorOnly && (
            <span className="page-sub" style={{ marginTop: 0, fontSize: 12 }}>
              毕业后想留在英国工作，需要雇主帮你办工签——这里只显示有这个资格的雇主。
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="eyebrow" style={{ minWidth: 56, marginBottom: 0 }}>行业</span>
          <Chip variant={industry === '' ? 'primary' : 'default'} onClick={() => patch({ ind: null })}>
            全部
          </Chip>
          {Object.entries(INDUSTRY_LABELS)
            .filter(([slug]) => (facets?.industry?.[slug] ?? 0) > 0)
            .sort((a, b) => (facets?.industry?.[b[0]] ?? 0) - (facets?.industry?.[a[0]] ?? 0))
            .map(([slug, label]) => (
              <Chip
                key={slug}
                variant={industry === slug ? 'primary' : 'default'}
                onClick={() => patch({ ind: industry === slug ? null : slug })}
              >
                {label} · {(facets?.industry?.[slug] ?? 0).toLocaleString()}
              </Chip>
            ))}
        </div>

        {/* 为你推荐 — signed-in only. Three states: anchors exist (toggle +
            the shared chip editor), extraction running (wait note), CV
            present but never analyzed (the one-click AI analysis). No CV →
            the import card below already carries the pitch. The keywords'
            proper home is the 简历 page; this row is the in-place remote. */}
        {/* 为你推荐 — filters by the combo terms the 简历 page's Cartesian
            builder produces (方向 × 职位). Read-only here: chips show each
            combo with its live hit count; editing lives on /my-cv. */}
        {signedIn && a.tfQ.isSuccess && (combos.length > 0 || !needsCv) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="eyebrow" style={{ minWidth: 56, marginBottom: 0 }}>推荐</span>
            {combos.length > 0 ? (
              <>
                <Chip
                  variant={rec ? 'primary' : 'default'}
                  onClick={() => patch({ rec: rec ? null : '1' })}
                  title="只显示命中这些组合的职位（组合 = 方向 × 职位，在「简历」页编辑）"
                >
                  ⭐ 为你推荐
                </Chip>
                {rec && (
                  <>
                    {combos.map((c) => (
                      <Chip key={c}>
                        {c}
                        {countsQ.data?.counts?.anchors?.[c] != null && ` · ${countsQ.data.counts.anchors[c]}`}
                      </Chip>
                    ))}
                    <Link to="/my-cv" className="page-sub" style={{ marginTop: 0, textDecoration: 'underline' }}>
                      编辑关键词 →
                    </Link>
                  </>
                )}
              </>
            ) : a.extractBusy ? (
              <span className="page-sub" style={{ marginTop: 0 }}>正在分析你的简历，大约需要一分钟，好了会自动出现…</span>
            ) : (
              <button className="btn" style={{ fontSize: 12.5 }} onClick={a.runExtract}>
                ⭐ 分析我的简历，启用推荐
              </button>
            )}
          </div>
        )}

        <form
          style={{ display: 'flex', gap: 8 }}
          onSubmit={(e) => { e.preventDefault(); patch({ q: q.trim() || null }); }}
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索职位名称或公司，例如：software、Deloitte"
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--line)', background: 'var(--surface)',
              color: 'var(--ink)', fontSize: 13,
            }}
          />
          <button className="btn" type="submit">搜索</button>
          {search && (
            <button className="btn ghost" type="button" onClick={() => { setQ(''); patch({ q: null }); }}>
              清除
            </button>
          )}
        </form>
      </div>

      {/* 导入简历引导 — signed-in users with no CV yet. The board is where
          they spend their time, so this is where the pitch for the CV lives:
          import one and the board can start matching jobs to the person. */}
      {needsCv && (
        <div className="card" style={{ marginBottom: 16, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderLeft: '3px solid var(--pri)' }}>
          <span style={{ fontSize: 18 }} aria-hidden="true">📄</span>
          <div style={{ flex: 1, minWidth: 220, fontSize: 13.5, lineHeight: 1.6 }}>
            <strong>导入你的简历</strong>
            <span style={{ color: 'var(--ink2)' }}>
              ，之后职位板可以按你的背景筛选推荐职位，评估和定制也会更准。没有英文简历也行——用中文写，AI 来写成英文。
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn accent" onClick={() => nav('/cv/new')}>用中文生成 →</button>
            <button className="btn" onClick={() => nav('/cv/upload')}>上传已有简历</button>
          </div>
        </div>
      )}

      {/* 结果 — placeholderData keeps the previous page rendered during a
          filter change, so the loading card only ever shows on the true first
          load; refetches dim the stale list instead of replacing it. */}
      {boardQ.isLoading && !boardQ.data && <div className="card">加载中…</div>}

      {boardQ.isError && (
        <div className="card">
          职位板暂时无法加载。<span className="page-sub">{String(boardQ.error)}</span>
        </div>
      )}

      {!boardQ.isLoading && !boardQ.isError && items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 15, marginBottom: 8 }}>没有符合条件的职位</div>
          <div className="page-sub">
            {rec
              ? '你的关键词没有命中任何职位——看看每个词后面的命中数，删掉 · 0 的词，或加几个更宽的（比如 security、data、engineer）。'
              : <>试试放宽筛选条件{(roleLevel || industry) && unknownCount > 0 && '，或打开「未分类」看看'}</>}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div style={{ opacity: boardQ.isFetching ? 0.55 : 1, transition: 'opacity 0.15s' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, margin: '4px 2px 10px' }}>
            <div className="page-sub">
              共 {total.toLocaleString()} 个职位
              {pages > 1 && ` · 第 ${page + 1} / ${pages} 页`}
            </div>
            {/* 语言切换 — only where the browser can actually translate. The
                same preference drives the JD card's chips on the detail page:
                two placements, one switch. */}
            {lang.supported && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="page-sub" style={{ marginTop: 0 }}>语言</span>
                <Chip variant={lang.zh ? 'default' : 'primary'} onClick={() => lang.setZh(false)}>原文</Chip>
                <Chip variant={lang.zh ? 'primary' : 'default'} onClick={() => lang.setZh(true)}>中文</Chip>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {items.map((i) => {
              const pay = salaryText(i);
              const posted = daysAgo(i.posted_at || i.first_seen);
              return (
                // The whole row navigates to the public detail page — the
                // MatchRow pattern (div + nav + stopPropagation on inner
                // controls), not a <Link>: the signed-in row embeds a real
                // button, and nesting interactive elements inside an anchor
                // is both invalid and fights the default navigation.
                <div
                  key={i.url}
                  className="row"
                  onClick={() => nav(`/jobs/${i.id}`)}
                  style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <JobTitle title={i.title} active={lang.active} style={{ fontWeight: 600, fontSize: 14 }} />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                      {i.company && (
                        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{displayCompany(i.company)}</span>
                      )}
                      {/* Separator only between two present values — some rows
                          genuinely have no company name. It is its own flex child
                          so the 8px gap lands on BOTH sides of it: baked into the
                          city string it got 8px before and a ~3px space after,
                          which read as a lopsided dot on every row of the board. */}
                      {i.city && i.company && <span className="page-sub" aria-hidden="true">·</span>}
                      {i.city && <span className="page-sub">{i.city}</span>}
                      {pay && <Chip variant="good">{pay}</Chip>}
                      {i.role_level && (
                        <Chip variant="primary">{ROLE_LABELS[i.role_level as RoleLevel] ?? i.role_level}</Chip>
                      )}
                      {i.industry && <Chip>{INDUSTRY_LABELS[i.industry] ?? i.industry}</Chip>}
                    </div>
                  </div>
                  {/* Right rail: everything anchored to the row's right edge
                      lives in ONE right-aligned column. The date used to sit
                      at the end of the title line while the signed-in button
                      was a separate vertically-centered sibling — two anchors
                      at different heights, nothing aligned (user screenshot,
                      2026-08-14). .page-sub carries margin-top: 6, hence the
                      explicit reset. */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 6, flexShrink: 0 }}>
                    {posted && (
                      <div className="page-sub" style={{ whiteSpace: 'nowrap', marginTop: 0, fontSize: 12.5 }}>{posted}</div>
                    )}
                    {/* Signed-in only: the fast lane into the user's own
                        pipeline. Anonymous rows end with the arrow — the
                        conversion pitch lives on the detail page, not here. */}
                    {signedIn && <AddToPipeline item={i} />}
                  </div>
                  <span className="row-arrow" aria-hidden="true">→</span>
                </div>
              );
            })}
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
              <button className="btn ghost" disabled={page === 0} onClick={() => patch({ page: String(page - 1) })}>
                ← 上一页
              </button>
              <span className="page-sub">{page + 1} / {pages}</span>
              <button className="btn ghost" disabled={page + 1 >= pages} onClick={() => patch({ page: String(page + 1) })}>
                下一页 →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
